// Shared constants & helpers for the Schedule module.

export const TIME_START = 7 * 60; // 7:00 AM
export const TIME_END = 16 * 60; // 4:00 PM
export const TOTAL_MIN = TIME_END - TIME_START;
export const SNAP = 5; // 5-minute increments
export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
export const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri"];

export const HOURS = (() => {
  const arr = [];
  for (let h = 7; h <= 16; h++) arr.push(h);
  return arr;
})();

// FP3-121 → FP3-123 — Distinct grade palette (K–12).
//
// Rewritten for FP3-123 because the FP3-121 palette had Grade 3
// (dusty lavender) and Grade 8 (plum) reading as two variants of
// purple, and Grades 5/6/7/8 (olive/teal/rose/plum) still crowded
// each other in dense schedules. The new palette walks the color
// wheel with clearly different hue families for every neighbor so
// a provider can identify a block by color alone at a glance:
//
//   K   → gold-yellow
//   1   → green
//   2   → blue
//   3   → violet
//   4   → orange
//   5   → olive
//   6   → teal
//   7   → coral
//   8   → rose/pink
//   9   → cyan
//   10  → brown/tan
//   11  → indigo/navy
//   12  → plum
//
// Grade 3 (violet #6E36BF) and Grade 8 (rose #D22C7C) are now from
// unambiguously different color families. Grades 5/6/7/8 span
// olive → teal → coral → rose — four distinct families instead of
// four green-adjacent muted variants.
//
// Each grade defines:
//   bg     — soft fill (safe for repeat all-day viewing)
//   border — mid-saturation stroke that reinforces grade identity;
//            block cards ALSO use this as the left accent bar so
//            the color reads even in narrow columns
//   text   — deep-saturation dark variant of the same hue; used for
//            student-name / duration text on the block. All 13
//            text/bg pairs clear WCAG AA (≥4.5:1) so names remain
//            readable in either the calm or dense view.
//
// Personal blocks (meetings, IEP writing, lunch, prep, testing,
// provider unavailable) DO NOT pull from this map — ScheduleBuilder
// swaps to a neutral gray tile so grade colors never contaminate
// provider personal blocks.
export const GRADE_COLORS = {
  K:    { bg: "#FBEFBB", border: "#C99A0E", text: "#5A4400" },
  "1":  { bg: "#D5EED9", border: "#3D9950", text: "#1A5A29" },
  "2":  { bg: "#CFE2F8", border: "#2E70C9", text: "#10416F" },
  "3":  { bg: "#E5D6F5", border: "#6E36BF", text: "#391571" },
  "4":  { bg: "#FCDCB8", border: "#DD6E0F", text: "#6B3200" },
  "5":  { bg: "#E4E7A9", border: "#8B9C1E", text: "#48520B" },
  "6":  { bg: "#C7EAE4", border: "#1B9789", text: "#084942" },
  "7":  { bg: "#FDD0C4", border: "#E14D31", text: "#6C1B08" },
  "8":  { bg: "#FBD3E2", border: "#D22C7C", text: "#6E1240" },
  "9":  { bg: "#C4EAF2", border: "#128AB4", text: "#054C63" },
  "10": { bg: "#EBD8C0", border: "#8D5A2E", text: "#482C10" },
  "11": { bg: "#D0D5EF", border: "#333F9E", text: "#101A5A" },
  "12": { bg: "#EAD0DC", border: "#883A5F", text: "#431628" },
};

// Ordered grade list for legend rendering + deterministic lookup.
export const GRADE_ORDER = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

// Fallback tile for unknown / missing grade (open / unassigned).
export const OPEN_COLOR = { bg: "#F1EFEA", border: "#B3AFA6", text: "#4B4740" };

export const CONFLICT_STYLES = {
  student_overlap: { bg: "bg-[#FDF2F2]", border: "border-[#B85C5C]", text: "text-[#B85C5C]", label: "Student Overlap" },
  provider_double_book: { bg: "bg-[#FDF8F3]", border: "border-[#D9A05B]", text: "text-[#D9A05B]", label: "Provider Double-Book" },
  room_conflict: { bg: "bg-[#EDF2F7]", border: "border-[#4A6B8C]", text: "text-[#4A6B8C]", label: "Room Conflict" },
  push_in_overlap: { bg: "bg-[#FDF2F2]", border: "border-[#B85C5C]", text: "text-[#B85C5C]", label: "Push-In Overlap" },
};

export const toMin = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export const toTime = (m) =>
  `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
};

export const fmtHour = (h) => `${h > 12 ? h - 12 : h}:00${h >= 12 ? " PM" : " AM"}`;

export const pct = (min) => ((min - TIME_START) / TOTAL_MIN) * 100;

// FP3-121 — Canonical grade-color helper. Normalizes the grade key
// (accepts "K"/"k", numeric, or "0" for kindergarten) so every
// schedule surface (Builder, Provider Calendar, Admin Shared
// Calendar month/day, Wizard preview) resolves the same tile.
export function gradeColor(grade) {
  if (grade === null || grade === undefined) return OPEN_COLOR;
  const raw = String(grade).trim();
  if (raw === "" || raw === "—") return OPEN_COLOR;
  if (raw === "K" || raw === "k" || raw === "0") return GRADE_COLORS.K;
  if (GRADE_COLORS[raw]) return GRADE_COLORS[raw];
  return OPEN_COLOR;
}
export const gc = (students) => gradeColor(students?.[0]?.grade);

// Format a Date as local YYYY-MM-DD (avoid toISOString which uses UTC and can
// shift the date by 1 day in negative-UTC timezones).
export function fmtLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export function getWeekDates(base = new Date()) {
  const d = new Date(base);
  const dow = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return Array.from({ length: 5 }, (_, i) => {
    const dd = new Date(mon);
    dd.setDate(mon.getDate() + i);
    return fmtLocalDate(dd);
  });
}

export function assignLanes(sessions) {
  const sorted = [...sessions].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const lanes = [];
  for (const s of sorted) {
    const sm = toMin(s.start_time);
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (toMin(lanes[i][lanes[i].length - 1].end_time) <= sm) {
        lanes[i].push(s);
        s._lane = i;
        placed = true;
        break;
      }
    }
    if (!placed) {
      s._lane = lanes.length;
      lanes.push([s]);
    }
  }
  return { sorted, count: Math.max(lanes.length, 1) };
}

// Group sessions sharing (date, start_time, end_time, provider_id) into one virtual
// session containing the union of students. Used by Today, Schedule View, and Live
// Session launch so a single "scheduled group" is represented consistently.
export function groupSessionsByTimeSlot(sessions) {
  const grouped = {};
  for (const s of sessions) {
    if (s.status === "cancelled") continue;
    const key = `${s.date}__${s.start_time}__${s.end_time}__${s.provider_id || ""}`;
    if (!grouped[key]) {
      grouped[key] = {
        ...s,
        id: s.id,
        session_ids: [],
        students: [],
        student_ids: [],
        is_grouped: false,
      };
    }
    const g = grouped[key];
    g.session_ids.push(s.id);
    for (const st of s.students || []) {
      if (!g.student_ids.includes(st.id)) {
        g.students.push(st);
        g.student_ids.push(st.id);
      }
    }
    if (s.status === "in-progress") g.status = "in-progress";
  }
  const result = Object.values(grouped);
  for (const g of result) {
    // Group flag is based on student count (not row count) so that duplicate session rows
    // covering the same student are not mislabelled as "group · 1".
    g.is_grouped = g.students.length > 1;
    if (g.is_grouped) g.session_type = "group";
  }
  return result.sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.start_time.localeCompare(b.start_time);
  });
}


// FP3-106D — Monthly-minutes normaliser for the Builder Students box.
//
// Mirrors the canonical wizard formula (see routes/wizard.py ~L3904):
//   expected_minutes = (
//       ob["monthly_minutes_target"]
//       if ob["kind"] == "monthly_minutes"
//       else ob["sessions_per_week"] * ob["duration_min"] * 4
//   )
//
// Frontend student shape: `service_obligations: [{kind, total_minutes,
// delivery:{sessions_per_period, minutes_per_session}}]`.
//
// Rules:
//   - kind ∈ {"minutes_per_month","monthly_minutes"} → total_minutes
//   - kind == "minutes_per_week" → total_minutes * 4
//   - kind == "weekly_sessions"  → delivery.sessions_per_period *
//                                  delivery.minutes_per_session * 4
//   - kind == "no_direct_service" → 0
//   - otherwise (fallback)       → total_minutes ?? (sessions_per_period *
//                                  minutes_per_session * 4) ?? null
//
// Returns `null` when nothing usable exists (Builder renders "—").
// Sums across all obligations on the record.
export function studentMonthlyMinutes(student) {
  const obs = Array.isArray(student?.service_obligations)
    ? student.service_obligations
    : [];
  if (!obs.length) return null;
  let total = 0;
  let sawAny = false;
  for (const ob of obs) {
    if (!ob) continue;
    const kind = (ob.kind || "minutes_per_week").toString();
    if (kind === "no_direct_service") {
      // Explicit consult-only row contributes 0; don't return early —
      // a student may have both consult and direct rows.
      sawAny = true;
      continue;
    }
    const stored = Number(ob.total_minutes ?? ob.value);
    const sessions = Number(ob.delivery?.sessions_per_period);
    const perSession = Number(ob.delivery?.minutes_per_session);
    let monthly = null;
    if (kind === "minutes_per_month" || kind === "monthly_minutes") {
      monthly = Number.isFinite(stored) ? stored : null;
    } else if (kind === "minutes_per_week") {
      monthly = Number.isFinite(stored) ? stored * 4 : null;
    } else if (kind === "weekly_sessions") {
      if (Number.isFinite(sessions) && Number.isFinite(perSession)) {
        monthly = sessions * perSession * 4;
      } else if (Number.isFinite(stored)) {
        // Legacy shape: `total_minutes` = weekly total for weekly_sessions.
        monthly = stored * 4;
      }
    } else {
      // Unknown/legacy kind — best-effort fallback.
      if (Number.isFinite(stored)) monthly = stored * 4;
      else if (Number.isFinite(sessions) && Number.isFinite(perSession)) {
        monthly = sessions * perSession * 4;
      }
    }
    if (monthly != null && Number.isFinite(monthly)) {
      total += monthly;
      sawAny = true;
    }
  }
  return sawAny ? total : null;
}

// Human-readable monthly-minutes label ("90 min/mo") with a calm dash
// when no commitment exists. Kept separate from `studentMonthlyMinutes`
// so tests can assert numeric values.
export function formatMonthlyMinutes(minutes) {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  return `${Math.round(minutes)} min/mo`;
}
